<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2" color="success">mdi-file-document-multiple</v-icon>
      {{ t.dashboard?.client_services_search || 'Client Services Search' }}
    </v-card-title>
    <v-card-text>
      <!-- Row 1: Search term -->
      <v-row align="center">
        <v-col cols="12" md="8">
          <v-text-field
            v-model="searchTerm"
            :label="t.dashboard?.cs_search_label || 'Search by service name, IMSI, MSISDN, description'"
            :hint="isMultilevelReseller ? (t.dashboard?.minimum_3_chars_or_filters || 'Minimum 3 characters or use the filters below') : ''"
            persistent-hint
            prepend-inner-icon="mdi-magnify"
            clearable
            density="compact"
            variant="outlined"
            @keyup.enter="search"
            @click="$event.target.select()"
          />
        </v-col>
        <v-col cols="12" md="4" class="d-flex gap-2">
          <v-btn color="primary" @click="search" :disabled="!canSearch">
            <v-icon class="mr-1">mdi-magnify</v-icon>
            {{ t.common?.search || 'Search' }}
          </v-btn>
          <v-btn color="secondary" variant="outlined" @click="resetFilters">
            {{ t.common?.reset || 'Reset' }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- Row 2: Client selection -->
      <v-row class="mt-2">
        <v-col cols="12" md="6">
          <ClientSearchFilter
            v-model="selectedClientId"
            v-model:include-subclients="includeSubclients"
            :show-include-subclients="true"
            :client-select-label="t.dashboard?.select_client || 'Select Client'"
            @hierarchy-loaded="onHierarchyLoaded"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-row>
            <v-col cols="6">
              <v-select
                v-model="selectedServiceTypeId"
                :items="serviceTypes"
                item-title="name"
                item-value="id"
                :label="t.dashboard?.service_type || 'Service Type'"
                clearable
                density="compact"
                variant="outlined"
                :loading="loadingServiceTypes"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="selectedStatusId"
                :items="statuses"
                item-title="name"
                item-value="id"
                :label="t.dashboard?.cs_status || 'CS Status'"
                clearable
                density="compact"
                variant="outlined"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { t as tRef } from '@/composables/useLanguages'
import ClientSearchFilter from '@/components/ClientSearchFilter.vue'
import { getServiceTypes } from '@/composables/useServiceTypesAPI'
import store from '@/store'

export default {
  name: 'ClientServicesSearchSection',
  components: { ClientSearchFilter },
  props: {
    config: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      searchTerm: '',
      selectedClientId: null,
      includeSubclients: true,
      selectedServiceTypeId: null,
      selectedStatusId: null,
      serviceTypes: [],
      loadingServiceTypes: false,
      hasSubclients: false,
      statuses: [
        { id: 1, name: 'Active' },
        { id: 2, name: 'Expired' },
        { id: 3, name: 'Waiting for Payment' },
        { id: 4, name: 'Suspended' },
        { id: 5, name: 'Terminated' },
        { id: 6, name: 'Refunded' }
      ]
    }
  },
  computed: {
    t() { return tRef.value },
    token() {
      return store.getters.getToken
    },
    isMultilevelReseller() {
      const ct = store.getters.getUser?.client_type_name?.toLowerCase() || ''
      return ct === 'multilevel'
    },
    canSearch() {
      if (!this.isMultilevelReseller) return true
      return (this.searchTerm?.trim().length >= 3) ||
             this.selectedClientId ||
             this.selectedServiceTypeId ||
             this.selectedStatusId
    }
  },
  async mounted() {
    await this.loadServiceTypes()
  },
  methods: {
    async loadServiceTypes() {
      this.loadingServiceTypes = true
      try {
        const result = await getServiceTypes(this.token)
        if (!result.error && result.service_types) {
          this.serviceTypes = result.service_types
        }
      } finally {
        this.loadingServiceTypes = false
      }
    },
    onHierarchyLoaded({ hasSubclients }) {
      this.hasSubclients = hasSubclients
    },
    search() {
      if (!this.canSearch) return

      const query = {}

      if (this.searchTerm?.trim().length >= 3) {
        query.search = this.searchTerm.trim()
      }
      if (this.selectedClientId) {
        query.client_id = this.selectedClientId
      }
      if (this.includeSubclients) {
        query.include_subclients = 'true'
      }
      if (this.selectedServiceTypeId) {
        query.service_type_id = this.selectedServiceTypeId
      }
      if (this.selectedStatusId) {
        query.status_id = this.selectedStatusId
      }

      this.$router.push({ path: '/clientServices', query })
    },
    resetFilters() {
      this.searchTerm = ''
      this.selectedClientId = null
      this.includeSubclients = true
      this.selectedServiceTypeId = null
      this.selectedStatusId = null
    }
  }
}
</script>
